import React from 'react';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';

import { useConfig } from 'Context/ConfigContext';
import SearchMyCourseProvider from 'Context/SearchMyCourseProvider';
import RedirectMyPath from 'Routes/Classroom/RedirectMyPath';
import GuidedPractice, {
  GuidedPracticeIntroduction,
  ExamResults,
  ExamRoom,
} from 'Routes/GuidedPractice';
import MyCourses from 'Routes/MyCourses';
import Classroom, { ClassroomLoading } from 'Routes/Classroom';
import Landing from 'Routes/Landing';
import Login from 'Routes/Auth/Login';
import LoginLoading from 'Routes/Auth/LoginLoading';
import LoginSuccess from 'Routes/Auth/LoginSuccess';
import NoMatch from 'Routes/NoMatch';
import NoMatchPaaS from 'Routes/NoMatchPaaS';
import Maintenance from 'Routes/Maintenance';
import LoginWithToken from 'Routes/Auth/LoginWithToken';
import LoginBypass from 'Routes/Auth/LoginBypass';
import { HealthCheck } from 'Routes/HealthCheck';
import RedirectMyGoal from 'Routes/Classroom/RedirectMyGoal';
import Logout from 'Routes/Auth/Logout';

import getAppLayout, { ClassroomLayout, BlankLayout } from './AppLayout';
import { PATHS } from './paths';

const redirectTo = (url) => {
  window.location.replace(url);
  return null;
};

const path = (root, sublink) => {
  return `${root}${sublink}`;
};

const getRoutes = ({
  isNative = false,
  isMaintenance,
  uiVariant,
  disableMyCoursesRoute,
  myCoursesUrl,
  disableLoginRoute,
  loginUrl,
  enableNativeAppRoute,
  myCourses,
}) => {
  if (isMaintenance) {
    return [
      {
        path: PATHS.maintenance,
        title: 'Maintenance',
        exact: true,
        roles: [],
        hideMenu: true,
        private: false,
        route: Maintenance,
        layout: BlankLayout,
      },
      {
        title: 'NoMatch',
        route: () => {
          return <Redirect to={PATHS.maintenance} />;
        },
      },
    ];
  }

  const nativeAppRoutes = enableNativeAppRoute
    ? [
        {
          path: PATHS.loginWithToken,
          hideMenu: true,
          private: false,
          route: LoginWithToken.component,
        },
        {
          path: path(PATHS.loginBypass, '/:provider'),
          hideMenu: true,
          private: false,
          route: LoginBypass,
        },
      ]
    : [];

  const baseRoute = [
    {
      path: PATHS.root,
      title: 'Home',
      roles: [],
      hideMenu: true,
      route: Landing.component,
      exact: true,
    },
    {
      path: PATHS.myCourses,
      title: 'คอร์สของฉัน',
      exact: true,
      roles: [],
      route: disableMyCoursesRoute ? () => redirectTo(myCoursesUrl) : MyCourses,
      private: true,
      layout: (props) => {
        const AppLayout =
          myCourses?.layoutType !== 'withFixedSidebar'
            ? getAppLayout(isNative, {
                contentContainerProps: { style: { paddingTop: 0, minHeight: '100vh' } },
              })
            : null;

        return (
          <SearchMyCourseProvider>
            {AppLayout ? <AppLayout {...props} /> : props.children}
          </SearchMyCourseProvider>
        );
      },
      footer: false,
    },
    {
      path: path(PATHS.classroom, '/:id'),
      title: 'Classroom',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: Classroom,
      layout: ClassroomLayout,
    },
    {
      path: path(PATHS.classroom, '/:id/subsection/:subsectionReferId'),
      title: 'Classroom',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: Classroom,
      layout: ClassroomLayout,
    },
    {
      path: path(PATHS.courseCode, '/:courseCode/subsection/:subsectionReferId'),
      title: 'Classroom Loading',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: ClassroomLoading,
      layout: BlankLayout,
    },
    {
      path: path(PATHS.courseCode, '/:courseCode'),
      title: 'Classroom Loading',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: ClassroomLoading,
      layout: BlankLayout,
    },
    {
      path: PATHS.mygoal,
      title: 'Redirect to MyGoal',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: RedirectMyGoal,
      layout: BlankLayout,
    },
    {
      path: PATHS.mypath,
      title: 'Redirect to MyPath',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: RedirectMyPath,
      layout: BlankLayout,
    },
    {
      path: PATHS.login,
      title: 'login',
      roles: [],
      hideMenu: true,
      private: false,
      route: disableLoginRoute
        ? ({ location }) => {
            const { pathname: from, search } = location.state?.from || {};
            const { p: provider } = queryString.parse(search);

            if (provider === 'mcv') {
              const { myCourseVille: { myCourseVilleLoginUrl } = {} } = useConfig();
              const classroomUrl = `${from}${search}`;
              const myCourseVilleUrl = `${myCourseVilleLoginUrl}?redirectTo=${classroomUrl}`;

              return redirectTo(myCourseVilleUrl);
            }
            return redirectTo(loginUrl);
          }
        : Login.component,
      layout: BlankLayout,
    },
    {
      path: PATHS.logout,
      title: 'logout',
      roles: [],
      hideMenu: true,
      private: false,
      route: Logout,
      layout: BlankLayout,
    },
    {
      path: PATHS.loginLoading,
      title: 'login-loading',
      roles: [],
      hideMenu: true,
      private: false,
      route: LoginLoading,
      layout: BlankLayout,
    },
    {
      path: PATHS.loginSuccess,
      title: 'login-success',
      roles: [],
      hideMenu: true,
      private: false,
      route: LoginSuccess,
      layout: BlankLayout,
    },
    {
      path: PATHS.healthCheck,
      title: 'Health check',
      exact: true,
      roles: [],
      hideMenu: true,
      private: false,
      route: HealthCheck,
      layout: BlankLayout,
    },
    {
      path: PATHS.guidedPractice,
      title: 'Guided Practice',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: GuidedPractice,
      layout: BlankLayout,
    },
    {
      path: path(PATHS.guidedPractice, '/:guidedPracticeUserId'),
      title: 'Guided Practice Introduction',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: GuidedPracticeIntroduction,
      layout: BlankLayout,
    },
    {
      path: path(
        PATHS.guidedPractice,
        '/:guidedPracticeUserId/exam-room/:examPaperId/session/:sessionId/results'
      ),
      title: 'Guided Practice Results',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: ExamResults,
      layout: BlankLayout,
    },
    {
      path: path(
        PATHS.guidedPractice,
        '/:guidedPracticeUserId/exam-room/:examPaperId/session/:sessionId'
      ),
      title: 'Guided Practice Exam Room',
      exact: true,
      roles: [],
      hideMenu: true,
      private: true,
      route: ExamRoom,
      layout: BlankLayout,
    },
    ...nativeAppRoutes,
    {
      title: 'NoMatch',
      roles: [],
      hideMenu: true,
      route: uiVariant === 'PAAS' ? NoMatchPaaS : NoMatch,
      layout: BlankLayout,
    },
  ];

  return baseRoute;
};

export default getRoutes;
